import { type ReactNode } from 'react';
import { useFormField, type useFormFieldResult } from '../../useFormField';
import { CheckboxGroupField, type CheckboxGroupFieldProps } from '../base-fields/CheckboxGroupField';
import type { StripControlledProperties } from '../../types';
import { Checkbox } from '../base-controls/Checkbox';
import { cn } from '~/shared/components/utils';

type OptionType =
	| string
	| {
			id: number | string;
			label: string;
	  };

type OptionStyle = {
	optionClassName: string;
	optionSelectedClassName: string | undefined;
};

export interface FormCheckboxGroupFieldProps extends StripControlledProperties<CheckboxGroupFieldProps> {
	name: string;
	options?: OptionType[];
	getOptions?: (fieldProps: useFormFieldResult<string[]>) => ReactNode;
	optionStyle?: OptionStyle;
	containerClassName?: string;
	onChangeOverride?: (value: string[] | number[], fieldProps: useFormFieldResult<string[]>) => Promise<void>;
}

function getOptionValue(option: OptionType): string {
	if (typeof option === 'string') {
		return option;
	}
	return option.id.toString();
}

function getOptionLabel(option: OptionType): string {
	if (typeof option === 'string') {
		return option;
	} else if ('label' in option) {
		return option.label;
	}
	throw new Error('Invalid option type');
}

function getOptionClassName(option: OptionType, isSelected: boolean, optionStyle?: OptionStyle): string | undefined {
	if (typeof option === 'string') {
		return undefined;
	} else if (optionStyle) {
		return cn(optionStyle.optionClassName, isSelected ? optionStyle.optionSelectedClassName : null, option);
	}
	return undefined;
}

export function CheckboxGroupFormField(props: FormCheckboxGroupFieldProps) {
	const fieldProps = useFormField<string[]>({ ...props, validateOnChange: true });

	const onSelectionChange = async (value: string[] | number[]) => {
		if (props.onChangeOverride) {
			await props.onChangeOverride(value, fieldProps);
			return;
		}
		const fieldValueStrings = value.map((x: string | number) => x.toString());
		await fieldProps.onChange(fieldValueStrings);
	};

	const fieldValue = fieldProps.value.length > 0 ? fieldProps.value.map(x => x.toString()) : [];

	const options =
		props.options && props.options.length
			? props.options.map(option => {
					const optionValue = getOptionValue(option);
					const optionLabel = getOptionLabel(option);
					const isOptionChecked = fieldProps.value.includes(optionValue.toString());

					const className = getOptionClassName(option, isOptionChecked, props.optionStyle);

					return <Checkbox key={optionValue} label={optionLabel} value={optionValue} className={className} isSelected={isOptionChecked} />;
			  })
			: props.getOptions
			? props.getOptions(fieldProps)
			: null;

	return (
		<CheckboxGroupField {...props} {...fieldProps} value={fieldValue} onChange={onSelectionChange}>
			<div className={cn('ml-1', props.containerClassName ?? 'grid grid-cols-1 gap-2')}>{options}</div>
		</CheckboxGroupField>
	);
}
